import { useCallback, useEffect, useMemo, useState } from "react";

import { Icon } from "../components/Icon.jsx";
import { DriverActionDialog } from "./DriverActionDialog.jsx";
import {
  getDriverStorageDiagnostics,
  getDriverSyncItems,
  retryAllRecoverableDriverSync,
  repairDriverMediaContext,
  retryDriverSyncItem,
  subscribeDriverSync,
  supersedeDriverSyncItem,
} from "./offline-store.js";

const TYPE_LABELS = {
  REORDER_STOPS: "Perubahan urutan rute",
  SAVE_LOADING: "Checklist loading",
  CONFIRM_LOADING: "Konfirmasi loading",
  START_TRIP: "Mulai perjalanan",
  START_STOP: "Mulai titik",
  COMPLETE_STOP: "Penyelesaian titik",
  FAIL_STOP: "Kendala titik",
  ODOMETER: "Foto odometer",
  VEHICLE: "Foto kendaraan",
  RECEIPT: "Foto struk",
  INCIDENT: "Foto kendala",
  VEHICLE_WASH_AFTER: "Foto setelah cuci",
  STOP_COMPLETION: "Foto bukti delivery",
};

function formatTime(value) {
  if (!value) return "Belum pernah dicoba";
  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Jakarta",
  }).format(new Date(value));
}

function formatStorage(bytes) {
  if (!Number.isFinite(Number(bytes))) return "Tidak tersedia";
  return `${(Number(bytes) / (1024 * 1024)).toLocaleString("id-ID", { maximumFractionDigits: 1 })} MB`;
}

function SyncDetailDialog({ online, onClose, onChanged, repairContext }) {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [message, setMessage] = useState("");
  const [storage, setStorage] = useState(null);
  const load = useCallback(async () => {
    const [nextItems, diagnostics] = await Promise.all([
      getDriverSyncItems(),
      getDriverStorageDiagnostics(),
    ]);
    setItems(nextItems);
    setStorage(diagnostics);
  }, []);

  useEffect(() => {
    load();
    return subscribeDriverSync(load);
  }, [load]);

  const recoverableCount = items.filter((item) => item.syncStatus !== "FAILED_PERMANENT").length;
  const repairableCount = items.filter((item) => item.kind === "media"
    && item.mediaKind === "STOP_COMPLETION"
    && item.lastErrorCode === "STOP_MEDIA_CONTEXT_REQUIRED").length;

  async function retryOne(item) {
    setBusyId(item.id);
    setMessage("");
    try {
      await retryDriverSyncItem(item.kind, item.id);
      await load();
      await onChanged?.();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusyId("");
    }
  }

  async function repairOne(item) {
    if (!repairContext?.fleetOrderId || !repairContext?.stopKey) return;
    const target = [repairContext.foNumber, repairContext.customerName].filter(Boolean).join(" · ");
    if (!window.confirm(`Hubungkan foto ini ke titik aktif${target ? ` (${target})` : ""}, lalu coba kirim kembali?`)) return;
    setBusyId(item.id);
    setMessage("");
    try {
      await repairDriverMediaContext(item.id, repairContext);
      await retryDriverSyncItem(item.kind, item.id);
      await load();
      await onChanged?.();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusyId("");
    }
  }

  async function repairAll() {
    const repairable = items.filter((item) => item.kind === "media"
      && item.mediaKind === "STOP_COMPLETION"
      && item.lastErrorCode === "STOP_MEDIA_CONTEXT_REQUIRED");
    if (!repairable.length || !repairContext?.fleetOrderId || !repairContext?.stopKey) return;
    const target = [repairContext.foNumber, repairContext.customerName].filter(Boolean).join(" · ");
    if (!window.confirm(`Hubungkan ${repairable.length} foto ke titik aktif${target ? ` (${target})` : ""}, lalu coba kirim kembali?`)) return;
    setBusyId("repair-all");
    setMessage("");
    try {
      for (const item of repairable) {
        await repairDriverMediaContext(item.id, repairContext);
      }
      await retryAllRecoverableDriverSync();
      await load();
      await onChanged?.();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusyId("");
    }
  }

  async function archiveOne(item) {
    if (!window.confirm("Arsipkan data ini dari antrean aktif? Data dan foto tetap disimpan di perangkat untuk bantuan teknis.")) return;
    setBusyId(item.id);
    try {
      await supersedeDriverSyncItem(item.kind, item.id);
      await load();
      await onChanged?.();
    } finally {
      setBusyId("");
    }
  }

  async function discardRouteConflict(item) {
    if (!window.confirm("Buang draft urutan lokal dan gunakan rute terbaru dari server? Checklist loading tidak akan dihapus.")) return;
    setBusyId(item.id);
    setMessage("");
    try {
      await supersedeDriverSyncItem(item.kind, item.id);
      await load();
      await onChanged?.();
      setMessage("Draft rute lokal dibuang. Buka kembali pengaturan rute untuk memuat urutan terbaru.");
    } finally {
      setBusyId("");
    }
  }

  async function retryAll() {
    setBusyId("all");
    setMessage("");
    try {
      await retryAllRecoverableDriverSync();
      await load();
      await onChanged?.();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusyId("");
    }
  }

  return (
    <DriverActionDialog
      actions={(
        <>
          <button className="driver-secondary-button" onClick={onClose} type="button">Tutup</button>
          <button className="driver-primary-button" disabled={!online || !recoverableCount || Boolean(busyId)} onClick={retryAll} type="button">
            <Icon name="sync" size={17} /> {busyId === "all" ? "Mencoba..." : "Coba semua"}
          </button>
        </>
      )}
      busy={Boolean(busyId)}
      description="Data dan foto tetap tersimpan di perangkat sampai berhasil dikirim."
      onClose={onClose}
      title="Status sinkronisasi"
    >
      {message && <div className="driver-error-banner" role="alert">{message}</div>}
      {!online && <div className="driver-offline-note"><Icon name="warning" size={17} /> Sambungkan internet untuk mencoba sinkronisasi.</div>}
      {storage && (
        <div className="driver-storage-diagnostics">
          <strong>Penyimpanan bukti foto</strong>
          <span>{formatStorage(storage.retainedBlobBytes)} tersimpan lokal · {storage.retainedBlobCount} foto</span>
          <small>
            Bukti yang sudah tersinkron disimpan {storage.retentionDays} hari. Foto yang belum berhasil dikirim tidak dihapus.
            {storage.quotaBytes ? ` Pemakaian aplikasi/peramban: ${formatStorage(storage.usageBytes)} dari ${formatStorage(storage.quotaBytes)}.` : ""}
          </small>
        </div>
      )}
      {repairableCount > 0 && repairContext?.fleetOrderId && repairContext?.stopKey && (
        <div className="driver-sync-context-repair">
          <div>
            <strong>{repairableCount} foto belum memiliki identitas titik</strong>
            <small>Foto dapat dihubungkan ke titik aktif tanpa mengambil ulang.</small>
          </div>
          <button disabled={!online || Boolean(busyId)} onClick={repairAll} type="button">
            {busyId === "repair-all" ? "Memperbaiki..." : "Gunakan untuk titik aktif"}
          </button>
        </div>
      )}
      <div className="driver-sync-item-list">
        {items.map((item) => {
          const permanent = item.syncStatus === "FAILED_PERMANENT";
          const pending = ["PENDING", "PROCESSING", "RETRY_SCHEDULED"].includes(item.syncStatus);
          const routeConflict = item.kind === "queue"
            && item.action === "REORDER_STOPS"
            && ["DRIVER_ROUTE_VERSION_CONFLICT", "DRIVER_ROUTE_STALE", "ROUTE_PREVIEW_STOP_SET_STALE"].includes(String(item.lastErrorCode || "").toUpperCase());
          return (
            <article className={`driver-sync-item ${permanent ? "is-permanent" : pending ? "is-pending" : "is-failed"}`} key={`${item.kind}-${item.id}`}>
              <span className="driver-sync-item__icon"><Icon name={item.kind === "media" ? "camera" : "document"} size={18} /></span>
              <div>
                <strong>{TYPE_LABELS[item.displayType] || (item.kind === "media" ? "Foto operasional" : "Data operasional")}</strong>
                <small>{item.fleetOrderId ? `Fleet Order ${item.fleetOrderId}` : `Referensi ${String(item.id).slice(-8)}`}</small>
                <p>{pending ? "Menunggu giliran sinkronisasi." : item.failureLabel}</p>
                <small>Percobaan {Number(item.lifetimeAttemptCount || item.attemptCount || 0)} · {formatTime(item.lastAttemptAt || item.createdAt)}</small>
                {item.correlationId && <small>Kode bantuan: {item.correlationId}</small>}
              </div>
              <div className="driver-sync-item__actions">
                {!permanent && <button disabled={!online || Boolean(busyId)} onClick={() => retryOne(item)} type="button">Coba lagi</button>}
                {permanent && item.kind === "media" && item.mediaKind === "STOP_COMPLETION" && item.lastErrorCode === "STOP_MEDIA_CONTEXT_REQUIRED" && repairContext?.fleetOrderId && repairContext?.stopKey && (
                  <button disabled={!online || Boolean(busyId)} onClick={() => repairOne(item)} type="button">Gunakan di titik ini</button>
                )}
                {permanent && routeConflict && <button disabled={Boolean(busyId)} onClick={() => discardRouteConflict(item)} type="button">Gunakan data terbaru</button>}
                {permanent && !routeConflict && <button disabled={Boolean(busyId)} onClick={() => archiveOne(item)} type="button">Arsipkan</button>}
              </div>
            </article>
          );
        })}
        {!items.length && <div className="driver-sync-empty"><Icon name="check" size={24} /><strong>Semua data sudah tersinkronisasi</strong></div>}
      </div>
    </DriverActionDialog>
  );
}

export function DriverSyncRecovery({ online, status, syncing, onChanged, repairContext }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);
  const pending = Number(status.pending || 0) + Number(status.mediaPending || 0);
  const failed = Number(status.failed || 0) + Number(status.mediaFailed || 0);
  const total = pending + failed;
  const retryable = pending + Number(status.recoverable || 0);
  const copy = useMemo(() => {
    if (syncing) return { tone: "is-syncing", title: "Menyinkronkan data", detail: "Data aman di perangkat selama proses berlangsung." };
    if (!online) return { tone: "is-offline", title: "Mode offline", detail: total ? `${total} data atau foto tersimpan di perangkat.` : "Data baru akan disimpan di perangkat." };
    if (failed) return { tone: "is-attention", title: `${failed} data perlu diperiksa`, detail: pending ? `${pending} lainnya masih menunggu sinkronisasi.` : "Data tidak dihapus dan dapat dipulihkan." };
    if (pending) return { tone: "is-pending", title: `${pending} menunggu sinkronisasi`, detail: "Akan dicoba otomatis saat koneksi stabil." };
    return null;
  }, [failed, online, pending, syncing, total]);

  if (!copy) return null;

  async function retryAll() {
    setRetryBusy(true);
    try {
      await retryAllRecoverableDriverSync();
      await onChanged?.();
    } finally {
      setRetryBusy(false);
    }
  }

  return (
    <>
      <section aria-live="polite" className={`driver-sync-recovery ${copy.tone}`}>
        <span className="driver-sync-recovery__icon">
          {syncing ? <span className="loading-spinner" /> : <Icon name={failed || !online ? "warning" : "sync"} size={18} />}
        </span>
        <div><strong>{copy.title}</strong><small>{copy.detail}</small></div>
        <div className="driver-sync-recovery__actions">
          {online && !syncing && retryable > 0 && <button disabled={retryBusy} onClick={retryAll} type="button">{retryBusy ? "Mencoba..." : "Coba lagi"}</button>}
          {total > 0 && <button onClick={() => setDetailsOpen(true)} type="button">Lihat detail</button>}
        </div>
      </section>
      {detailsOpen && <SyncDetailDialog online={online} onChanged={onChanged} onClose={() => setDetailsOpen(false)} repairContext={repairContext} />}
    </>
  );
}
